'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import { Formik } from 'formik'
import React, { useCallback, useEffect, useState } from 'react'

// walrus
import Box from '@components/kit/box'
import { BoxLink } from '@components/kit/link'
import Text from '@components/kit/text'
import submitForm from '@lib/submit-form'

// lib
import DataTable, { DataTableColumn } from '@components/ui/data-table'
import SectionHeader from '@components/ui/section-header'
import { useMemberSavingsStore } from '@store/MemberSavings'
import { IMemberSavingsDashboardItem } from '@store/MemberSavings/types'
import {
  DashboardFilterOption,
  toFilterOptions
} from './dashboard-filter-options'
import ReportsFilterSelect from './reports-filter-select.web'

/* -----------------------------------------------------------------------------
 * types
 * -------------------------------------------------------------------------- */

export type MemberSavingsDashboardFormValues = {
  company_ids?: number[]
  professional_ids?: number[]
}

/* -----------------------------------------------------------------------------
 * helpers
 * -------------------------------------------------------------------------- */

const formatSavings = (value: number) =>
  `$${value.toLocaleString('en-US', { maximumFractionDigits: 2 })}`

/* -----------------------------------------------------------------------------
 * columns
 * -------------------------------------------------------------------------- */

const columns: DataTableColumn<IMemberSavingsDashboardItem>[] = [
  {
    key: 'user_id',
    label: 'Member',
    render: row => (
      <BoxLink path={`/chat/${row.user_id}`}>
        <Text font='pro.text.13.medium' textColor='proAccent'>
          {row.user_id}
        </Text>
      </BoxLink>
    ),
    width: 110
  },
  {
    flex: 1,
    key: 'company_name',
    label: 'Employer',
    minWidth: 200,
    render: row => (
      <Text font='pro.text.13.regular' textColor='proTextBody' truncate>
        {row.company_name}
      </Text>
    )
  },
  {
    key: 'total_client_cost_savings',
    label: 'Client cost savings',
    render: row => (
      <Text font='pro.text.13.medium' textColor='proTeal'>
        {formatSavings(row.total_client_cost_savings)}
      </Text>
    ),
    width: 180
  },
  {
    key: 'total_member_cost_savings',
    label: 'Member cost savings',
    render: row => (
      <Text font='pro.text.13.medium' textColor='proTeal'>
        {formatSavings(row.total_member_cost_savings)}
      </Text>
    ),
    width: 190
  },
  {
    flex: 1,
    key: 'professional_match',
    label: 'PharmD',
    minWidth: 180,
    render: row => (
      <Text font='pro.text.13.regular' textColor='proTextMuted' truncate>
        {row.professional_match}
      </Text>
    )
  }
]

/* -----------------------------------------------------------------------------
 * FormikMemberSavingsDashboard
 * -------------------------------------------------------------------------- */

export function FormikMemberSavingsDashboard({
  companies,
  isLoading,
  professionals,
  results
}: {
  companies: DashboardFilterOption[]
  isLoading: boolean
  professionals: DashboardFilterOption[]
  results: IMemberSavingsDashboardItem[]
}) {
  return (
    <Box mb='40'>
      <SectionHeader
        count={results.length}
        description='Pending savings identified for members you are matched with'
        title='Member savings'
      />

      <Box
        alignItems='flex-start'
        flexDirection='row'
        flexWrap='wrap'
        gap='12'
        mb='12'
      >
        <Box width={220}>
          <ReportsFilterSelect
            filterOptions={companies}
            idKey='id'
            label='EMPLOYER'
            name='company_ids'
          />
        </Box>
        <Box width={220}>
          <ReportsFilterSelect
            filterOptions={professionals}
            idKey='id'
            label='PHARMD'
            name='professional_ids'
          />
        </Box>
      </Box>

      <DataTable<IMemberSavingsDashboardItem>
        columns={columns}
        data={results}
        emptyDescription='No pending member savings found.'
        emptyTitle='Nothing to review'
        getRowKey={row => String(row.user_id)}
        isLoading={isLoading}
      />
    </Box>
  )
}

/* -----------------------------------------------------------------------------
 * MemberSavingsDashboard
 * -------------------------------------------------------------------------- */

export default function MemberSavingsDashboard() {
  const { getMemberSavingsDashboard } = useMemberSavingsStore.getState()
  const [professional_ids, setProfessionalIds] = useState<number[]>([])
  const [company_ids, setCompanyIds] = useState<number[]>([])
  const [results, setResults] = useState<IMemberSavingsDashboardItem[]>([])
  const [professionals, setProfessionals] = useState<DashboardFilterOption[]>(
    []
  )
  const [companies, setCompanies] = useState<DashboardFilterOption[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const hasFilters = professional_ids.length > 0 || company_ids.length > 0

  const fetchDashboard = useCallback(async () => {
    setIsLoading(true)
    const data = await getMemberSavingsDashboard({
      professional_ids,
      company_ids
    })
    setResults(data)
    // filter options come from the unfiltered rows, so narrowing the dashboard
    // does not narrow the dropdowns along with it
    if (!hasFilters) {
      setProfessionals(
        toFilterOptions(
          data,
          item => item.professional_id,
          item => item.professional_match
        )
      )
      setCompanies(
        toFilterOptions(
          data,
          item => item.company_id,
          item => item.company_name
        )
      )
    }
    setIsLoading(false)
  }, [getMemberSavingsDashboard, professional_ids, company_ids, hasFilters])

  useEffect(() => {
    fetchDashboard()
  }, [fetchDashboard])

  const handleFormSubmit = submitForm(
    async (values: MemberSavingsDashboardFormValues) => {
      setProfessionalIds(values.professional_ids ?? [])
      setCompanyIds(values.company_ids ?? [])
    }
  )

  return (
    <Formik<MemberSavingsDashboardFormValues>
      initialValues={{ company_ids: [], professional_ids: [] }}
      onSubmit={handleFormSubmit}
    >
      <FormikMemberSavingsDashboard
        companies={companies}
        isLoading={isLoading}
        professionals={professionals}
        results={results}
      />
    </Formik>
  )
}
